// Inline Dynamic Agent Engine markdown (Option A)
// Note: using ~~~ fenced code blocks (instead of ``` ) to avoid backtick escaping issues in template literals.

const daeDocs = `
# Dynamic Agent Engine powered by ASI1-Mini and ASI1-Extended

Dynamic Agentic System (DAS) is Fetch.ai's unified runtime for planning multi-step tasks, calling tools (web search, retrieval, code execution), and streaming intermediate results. It pairs ASI1‑Mini and ASI1‑Extended with the Executable Agentic Expert Model (EAEM) architecture to turn prompts into verifiable, reproducible workflows.

Key points at a glance:
- Dynamic planning and re-planning with transparent plan output
- Native tool-use with citation tracking and source verification
- Secure Python execution for data analysis and visualization
- Real-time streaming (SSE) for low-latency, progressive responses
- Structured intermediate_stages for auditability and debugging
- Production endpoints with consistent request/response schemas

Note on naming: External materials should use “Dynamic Agentic System (DAS)”. The code_interpreter identifier in current endpoints and model IDs is maintained for compatibility.

## Executive Summary

The **ASI1-Mini Code Interpreter V2** represents a revolutionary advancement in AI architecture, implementing a sophisticated **Dynamic Agent Engine** that fundamentally transforms how AI systems operate. Unlike traditional Large Language Models (LLMs) that provide static, single-pass responses, this system creates an intelligent, multi-step agentic workflow capable of **planning, executing, adapting, and learning in real-time**.

Built on the proprietary **Executable Agentic Expert Model (EAEM)** architecture by **Fetch.ai Inc**, this system represents the first AI model specifically designed for **decentralized agentic workflows**, making it superior to conventional models from OpenAI, Google, Anthropic, and other competitors.

---

## Architecture Overview

### Core Engine Design - The EAEM Model

The Dynamic Agent Engine is built on the **Executable Agentic Expert Model (EAEM)** architecture, which consists of several interconnected components:

~~~mermaid
graph TD
    A[User Request] --> B[Request Validation & Preprocessing]
    B --> C[Dynamic Planning Engine]
    C --> D{Plan Requires Tools?}
    D -->|Yes| E[Tool Selection & Execution]
    D -->|No| F[Direct Response Generation]
    E --> G[Result Processing & Integration]
    G --> H{More Actions Needed?}
    H -->|Yes| C
    H -->|No| I[Final Response Synthesis]
    F --> I
    I --> J[Streaming/Standard Response]
~~~

### Key Architectural Components

1. **Planning Engine**
   - Analyzes user requests to determine optimal action sequences
   - Creates dynamic execution plans based on context and requirements
   - Supports reasoning mode for complex problem-solving

2. **Tool Execution Framework**
   - Executes selected tools with timeout protection (52 seconds)
   - Handles multiple tool types simultaneously
   - Provides error recovery and retry mechanisms

3. **Knowledge Integration System**
   - Formats and integrates tool results into structured knowledge bases
   - Provides citation tracking and source verification
   - Maintains context across multiple iterations

4. **Streaming Response Engine**
   - Real-time response streaming with Server-Sent Events (SSE)
   - Word-by-word delivery for enhanced user experience
   - Support for different content types (plan, content, tool_response)

---

## How It Works: The Multi-Step Agentic Process

### Phase 1: Request Analysis & Validation
- **Input Processing**: Validates message format, model compatibility, and parameters
- **Context Window Management**: Handles up to 64,000 tokens with intelligent truncation
- **Message Type Detection**: Automatically detects LLM vs VLM (Vision Language Model) requirements

### Phase 2: Dynamic Planning
The system enters a sophisticated planning phase where it:
- Analyzes the user's request complexity
- Determines required tools and execution sequence
- Creates a step-by-step execution plan
- Streams the plan to users in real-time for transparency

### Phase 3: Agent/Tool Execution Loop
The engine can execute up to **20 iterations** of tool calls, featuring:
- **Parallel Tool Processing**: Multiple agents/tools can be invoked simultaneously
- **Timeout Protection**: 52-second timeout per tool execution
- **Error Recovery**: Automatic retry mechanisms for failed operations
- **Result Integration**: Intelligent merging of tool outputs

### Phase 4: Knowledge Synthesis
- **Citation Tracking**: Each piece of information receives unique cite IDs
- **Source Verification**: All results are marked with verification status
- **Context Preservation**: Maintains conversation context across iterations

### Phase 5: Response Generation
- **Final Synthesis**: Combines all gathered information into coherent responses
- **Format Adaptation**: Supports various output formats (JSON, plain text, structured data)
- **Streaming Delivery**: Real-time response delivery with progress indicators

---

## Advanced Features & Capabilities

### 1. **Multi-Modal Tool Integration**
- **Google Search & News**: Real-time web information retrieval
- **Webpage Reader**: Content extraction and analysis from URLs
- **Jupyter Notebook Code Executor**: Live Python code execution with result visualization
- **Extensible Framework**: Easy addition of new agents/tools and capabilities

### 2. **Intelligent Result Processing**
- **Answer Box Extraction**: Prioritizes featured snippets and direct answers
- **Content Summarization**: Intelligent filtering of relevant information
- **Citation Management**: Automatic source attribution and verification
- **Knowledge Base Formatting**: Structured XML-based information storage

### 3. **Advanced Streaming Capabilities**
- **Real-time Planning Visibility**: Users see the AI's thought process
- **Progressive Tool Execution**: Live updates during tool operations
- **Error Transparency**: Clear communication of issues and recovery attempts
- **Multi-format Streaming**: Support for different content types

### 4. **Robust Error Handling**
- **Graceful Degradation**: Continues operation even when some tools fail
- **Retry Mechanisms**: Automatic recovery from transient failures
- **Error State Management**: Intelligent handling of failure scenarios
- **User-friendly Error Messages**: Clear communication of issues

### 5. **Context & Memory Management**
- **Large Context Window**: 64,000 token capacity for extended conversations
- **Intermediate Step Tracking**: Complete audit trail of all operations
- **Message Filtering**: Intelligent removal of internal processing messages
- **State Persistence**: Maintains conversation state across interactions

---

## Technical Specifications

| Specification | Value | Description |
|---------------|-------|-------------|
| **Context Window** | 64,000 tokens | Maximum input context size |
| **Generation Limit** | 8,192 tokens | Maximum response length |
| **Tool Timeout** | 52 seconds | Maximum execution time per tool |
| **Max Iterations** | 20 loops | Maximum planning-execution cycles |
| **Search Results** | 20 items | Maximum search results per query |
| **Streaming** | Real-time SSE | Server-Sent Events support |
| **Temperature Range** | 0.01 - 1.3 | Response creativity control |

---

## Benefits Over Traditional LLMs

### 1. **Dynamic vs Static Processing**
**Traditional LLMs** (ChatGPT, Claude, Gemini):
- Single-pass processing
- Limited real-time information access
- No tool execution capabilities
- Static knowledge cutoff dates

**ASI1-Mini Dynamic Engine**:
- Multi-iteration processing with adaptive planning
- Real-time web search and data retrieval
- Live code execution and analysis
- Dynamic knowledge integration

### 2. **Transparency & Explainability**
**Traditional LLMs**:
- Black-box decision making
- No visibility into reasoning process
- Limited source attribution

**ASI1-Mini Dynamic Engine**:
- Complete visibility into planning phase
- Real-time streaming of thought processes
- Comprehensive citation and source tracking
- Audit trail of all operations

### 3. **Tool Integration & Execution**
**Traditional LLMs**:
- Limited to text generation
- No real-time data access
- No code execution capabilities

**ASI1-Mini Dynamic Engine**:
- Native tool integration framework
- Real-time web search and content retrieval
- Live Python code execution
- Extensible tool ecosystem

### 4. **Error Handling & Recovery**
**Traditional LLMs**:
- Single point of failure
- No retry mechanisms
- Limited error context

**ASI1-Mini Dynamic Engine**:
- Graceful degradation
- Automatic retry mechanisms
- Comprehensive error reporting
- Recovery strategies

---

## Comparison with Leading AI Models

### ChatGPT (OpenAI)
| Aspect | ChatGPT | ASI1-Mini Dynamic Engine |
|--------|---------|--------------------------|
| **Architecture** | Transformer-based, static | Transformer + EAEM-based, dynamic agentic |
| **Tool Access** | Limited plugins | Native tool integration |
| **Planning** | Single-shot | Multi-iteration with replanning |
| **Real-time Data** | Limited web browsing | Full web search & analysis |
| **Code Execution** | Text-based only | Live Jupyter notebook execution |
| **Transparency** | Minimal | Complete process visibility |
| **Streaming** | Basic text streaming | Multi-type content streaming |

### Claude (Anthropic)
| Aspect | Claude | ASI1-Mini Dynamic Engine |
|--------|--------|--------------------------|
| **Safety Focus** | Constitutional AI | Task-focused execution |
| **Tool Integration** | Basic function calling | Advanced executable framework |
| **Planning Capability** | Single reasoning chain | Dynamic multi-step planning |
| **Web Access** | No native web access | Real-time search & content retrieval |
| **Code Execution** | Simulation only | Live execution environment |
| **Error Recovery** | Limited | Comprehensive retry mechanisms |
| **Result Integration** | Text-based | Structured knowledge bases |

### Gemini (Google)
| Aspect | Gemini | ASI1-Mini Dynamic Engine |
|--------|--------|--------------------------|
| **Multimodal** | Native image/text | Extensible to VLM |
| **Search Integration** | Google Search access | Multi-source web integration |
| **Code Capabilities** | Code generation | Live code execution & analysis |
| **Planning** | Single-pass reasoning | Multi-iteration agentic workflows |
| **Tool Ecosystem** | Google services focused | Open, extensible framework |
| **Streaming** | Standard response | Multi-type content streaming |
| **Citation** | Basic linking | Comprehensive source tracking |

---

## Use Cases & Applications

### 1. **Research & Analysis**
- **Academic Research**: Multi-source information gathering with citation tracking
- **Market Analysis**: Real-time data collection and trend analysis
- **Competitive Intelligence**: Web-based research with source verification

### 2. **Software Development**
- **Code Development**: Live testing and execution of code snippets
- **Debugging**: Interactive problem-solving with tool assistance
- **Documentation**: Automated research and content generation

### 3. **Data Science & Analytics**
- **Data Exploration**: Live Python execution for data analysis
- **Visualization**: Dynamic chart and graph generation
- **Statistical Analysis**: Real-time computation and validation

### 4. **Content Creation**
- **Research-backed Writing**: Multi-source fact-checking and citation
- **Technical Documentation**: Code examples with live validation
- **Educational Content**: Interactive examples and explanations

---

## Implementation Guide

### Basic Usage
~~~javascript
const response = await fetch('<api-endpoint>', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    model: '<model-name>',
    messages: [
      {
        role: 'user',
        content: 'Search for the latest developments in quantum computing and create a Python visualization of the trends'
      }
    ],
    stream: false,
    temperature: 0.7,
    max_tokens: 8192
  })
});
~~~

---

## Conclusion

The **ASI1-Mini Code Interpreter V2** with its Dynamic Agent Engine represents a paradigm shift in AI system design. By implementing the **Executable Agentic Expert Model (EAEM)**, it transcends the limitations of traditional LLMs, offering:

- **Dynamic, multi-step processing** vs static single-pass responses
- **Real-time tool integration** vs limited or no tool access
- **Complete transparency** vs black-box operations
- **Advanced error recovery** vs single points of failure
- **Extensible architecture** vs closed ecosystems

This system doesn't just generate text—it **thinks, plans, executes, and adapts**, making it the most advanced AI agent architecture available today. The combination of sophisticated planning, real-time tool execution, and intelligent result synthesis creates an AI system that can handle complex, multi-faceted tasks with unprecedented capability and reliability.

---

### Key Innovations
- **First AI model designed specifically for agentic implementations**
- **Proprietary EAEM architecture distinct from competitor approaches**
- **Focus on understanding, instruction-following, and agent/tool-calling capabilities**

The ASI1-Mini and ASI1-Extended models represent the cutting edge of this technology, offering unprecedented capabilities in dynamic planning, real-time execution, and intelligent adaptation that set new standards for what AI systems can achieve.
`;

export default daeDocs;
